/**
 * @file ComboBox.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-10
 * @modified 2026-09-16
 * @description Combo box that allows both selection and manual text entry
 *
 *              Options follow SearchableDropdown's contract: `label` is the
 *              plain string that goes in the input and is matched when typing,
 *              `searchText` widens that match, and `renderLabel` supplies rich
 *              content for the list row. Keeping `label` a string is what lets
 *              the input show a clean value while the row shows badges.
 */

import React, { useState, useRef, useEffect, useId } from 'react';
import './SearchableDropdown.css';
import useDropdownKeys from '../hooks/useDropdownKeys';

function ComboBox({
  options,
  value,
  onChange,
  placeholder = "Select or type...",
  searchPlaceholder = "Search...",
  disabled = false,
  // Open above the field. Fixed rather than measured: this sits at the bottom
  // of a scrolling panel, where an auto-flip would change direction as the
  // panel moves under it.
  dropUp = false,
  className = '',
  // Called with the whole option when one is PICKED from the list, so a caller
  // can keep what the option meant and not just its value. Not called for typed
  // text, which has no option behind it — that is the distinction a caller
  // needs to tell "chose the £262 Spot / 23T+ suggestion" from "typed 262".
  onSelectOption
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Set input value when value prop changes
    const selectedOption = options.find(opt => opt.value === value);
    if (selectedOption) {
      setInputValue(selectedOption.label);
    } else {
      setInputValue(value || '');
    }
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [isTyping, setIsTyping] = useState(false);

  const filteredOptions = options.filter(option => {
    // If dropdown just opened and user hasn't started typing, show all options
    if (!isTyping || !inputValue) return true;
    const search = inputValue.toLowerCase();
    return String(option.label ?? '').toLowerCase().includes(search) ||
           String(option.searchText ?? '').toLowerCase().includes(search) ||
           String(option.value ?? '').toLowerCase().includes(search);
  });

  // The keyboard cursor. It starts at -1 — on nothing — and stays there until
  // an arrow key moves it, because a ComboBox takes free text: someone who types
  // "262" and presses Tab means 262, not whichever suggestion happened to be at
  // the top of the list.
  const [cursor, setCursor] = useState(-1);
  const optionsRef = useRef(null);
  const listId = `combobox-list-${useId()}`;

  const { onMenuKeyDown, openFromKeyboard } = useDropdownKeys({
    isOpen,
    setIsOpen,
    navigable: filteredOptions,
    cursor,
    setCursor,
    onChoose: (option) => {
      setInputValue(option.label);
      if (onSelectOption) onSelectOption(option);
      else onChange(option.value);
      setIsTyping(false);
    },
    triggerRef: inputRef,
    listRef: optionsRef,
  });

  // The input IS the trigger and a text field, so most keys belong to it:
  // Space, Enter, characters, ← → Home End all edit the text. Only ↓ ↑ open
  // the list, and only an open list takes Escape, Enter and Tab.
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIsTyping(false);
        openFromKeyboard();
      }
      return;
    }
    if (e.key === 'Home' || e.key === 'End') return;
    onMenuKeyDown(e);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setIsTyping(true);
    setCursor(-1);
    // For manual entry, pass the text value directly
    onChange(newValue);
  };

  const handleSelect = (option) => {
    setInputValue(option.label);
    if (onSelectOption) onSelectOption(option);
    else onChange(option.value);
    setIsOpen(false);
    setIsTyping(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Don't filter when just focusing - show all options
    setIsTyping(false);
    setCursor(-1);
  };

  const handleButtonClick = () => {
    setIsOpen(!isOpen);
    // When clicking button, show all options
    setIsTyping(false);
  };

  return (
    <div className={`searchable-dropdown combobox${dropUp ? ' drop-up' : ''}${className ? ` ${className}` : ''}`} ref={dropdownRef}>
      <div className="dropdown-trigger-input">
        <input
          ref={inputRef}
          type="text"
          className="combo-input"
          placeholder={placeholder}
          value={inputValue}
          onChange={disabled ? undefined : handleInputChange}
          onFocus={disabled ? undefined : handleInputFocus}
          onKeyDown={disabled ? undefined : handleKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-controls={listId}
          aria-expanded={isOpen ? 'true' : 'false'}
          disabled={disabled}
          readOnly={disabled}
        />
        <button
          className="dropdown-toggle-btn"
          onClick={disabled ? undefined : handleButtonClick}
          type="button"
          disabled={disabled}
          // Out of the tab order: the input beside it already opens the list,
          // and stopping on the chevron makes every ComboBox two Tab presses.
          tabIndex={-1}
        >
          <span className="material-symbols-rounded">{isOpen ? 'expand_less' : 'expand_more'}</span>
        </button>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div className="dropdown-menu">
          <div className="dropdown-options" ref={optionsRef} id={listId} role="listbox">
            {filteredOptions.map((option, index) => (
              <div
                key={`${option.value}-${index}`}
                className={`dropdown-item ${value === option.value ? 'active' : ''} ${cursor === index ? 'is-cursor' : ''}`}
                data-cursor={cursor === index ? 'true' : undefined}
                // No onMouseEnter here, unlike the other dropdowns: a pointer
                // resting over the list must not move the cursor, or Tab would
                // swap typed text for whichever row the mouse was over.
                onClick={() => handleSelect(option)}
              >
                {option.renderLabel || option.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ComboBox;
